import { useSyncExternalStore } from 'react';

/**
 * ⭐ Nutrition's own colours (owner, 2026-10-21 — the cinematic pass). Every instrument has its
 * subject colour, as Hydration has its water cyan: Nutrition's is SAFFRON — the plate's light,
 * what's left, the one solid button. Personal's red goes back to wayfinding only (the lens pill,
 * the Stats | Actions toggle, the section tab underline, the header icon), so eating progress
 * never reads as an error.
 *
 * The macros are the "spice pantry" (owner's pick, 2026-10-21): rust protein, za'atar carbs,
 * cinnamon fat — warm, so they sit with saffron, and far enough apart (from saffron, from each
 * other, from red) that no macro reads as calories. Rose and pistachio were tried and rejected.
 *
 * Every value has a LIGHT twin chosen for contrast on the light card (marks ≥ 3:1, text ≥ 4.5:1):
 * saffron becomes copper; where a mark colour is too pale for text there is a separate text tone.
 * Components take the hex for the current theme from the hooks below (hex, so the existing
 * `${color}1f` tints and `readableOn(color)` ink keep working).
 */

export type MacroKey = 'protein' | 'carbs' | 'fat';

interface Palette {
  /** Nutrition's accent: marks, fills, the remaining figure. */
  accent: string;
  /** Macro marks: rings, bars, dots. */
  macro: Record<MacroKey, string>;
  /** A macro's name or figure set IN its colour (≥ 4.5:1 on the card). */
  macroText: Record<MacroKey, string>;
}

export const NUTRITION_PALETTE: { dark: Palette; light: Palette } = {
  dark: {
    accent: '#F5B544',
    macro: { protein: '#A93F27', carbs: '#B5B898', fat: '#AC7E58' },
    macroText: { protein: '#C76C57', carbs: '#B5B898', fat: '#AC7E58' },
  },
  light: {
    accent: '#A3570A',
    macro: { protein: '#A22F46', carbs: '#8B915F', fat: '#5D3B16' },
    macroText: { protein: '#A22F46', carbs: '#6A6D38', fat: '#5D3B16' },
  },
};

/* The theme is the `dark` class on <html> (useTheme toggles it) — the same switch Hydration's
   CSS reads (`html:not(.dark)`), so a page never mixes a dark colour with a light card. */
function subscribe(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  const mo = new MutationObserver(onChange);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => mo.disconnect();
}
const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

/** True while the app is in its dark theme; re-renders when the theme changes. */
export function useIsDarkTheme(): boolean {
  return useSyncExternalStore(subscribe, isDark, () => true);
}

/** Nutrition's palette for the current theme. */
export function useNutritionPalette(): Palette {
  return useIsDarkTheme() ? NUTRITION_PALETTE.dark : NUTRITION_PALETTE.light;
}

/** Nutrition's accent (saffron in dark, copper in light). */
export function useNutritionAccent(): string {
  return useNutritionPalette().accent;
}
